import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { msalInstance, loginRequest } from "./lib/auth";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Documents from "./pages/Documents";
import DocumentDetail from "./pages/DocumentDetail";
import Upload from "./pages/Upload";
import AgentChat from "./pages/AgentChat";
import BatchUpload from "./pages/BatchUpload";
import Guide from "./pages/Guide";
import Audit from "./pages/Audit";
import FloatingAgent from "./components/FloatingAgent";
import UploadStatusBar from "./components/UploadStatusBar";
import { UploadProvider, useUpload } from "./lib/UploadContext";
import "./styles/global.css";
import NeuroGenLogo from "./components/NeuroGenLogo";
import AuditPreview from "./pages/AuditPreview";
import AdminQAChecks from "./pages/AdminQAChecks";
import AdminIdrPrompt from "./pages/AdminIdrPrompt";
import IPReporting from './pages/IPReporting';
import IDRReporting from './pages/IDRReporting';
import MSOReporting from './pages/MSOReporting';
import MyReport from './pages/MyReport';
import AdminUsers from './pages/AdminUsers';
import { useRoles } from "./lib/useRoles";
import { TechIdentityProvider } from "./lib/TechIdentityContext";

function LoginPage() {
  const { instance } = useMsal();
  return (
    <div style={{
      height:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
      position:"relative", overflow:"hidden",
      background:"linear-gradient(160deg, #0F1B2E 0%, #162544 40%, #1B2A4A 70%, #0F1B2E 100%)"
    }}>
      {/* Background neural network decoration */}
      <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0.4 }} viewBox="0 0 800 600" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="80" y1="120" x2="200" y2="80" stroke="#4A90D9" strokeWidth="0.5" opacity="0.15"/>
        <line x1="200" y1="80" x2="300" y2="150" stroke="#4A90D9" strokeWidth="0.5" opacity="0.15"/>
        <line x1="80" y1="120" x2="150" y2="250" stroke="#4A90D9" strokeWidth="0.5" opacity="0.1"/>
        <line x1="600" y1="100" x2="720" y2="160" stroke="#4A90D9" strokeWidth="0.5" opacity="0.15"/>
        <line x1="720" y1="160" x2="680" y2="280" stroke="#4A90D9" strokeWidth="0.5" opacity="0.12"/>
        <line x1="600" y1="100" x2="550" y2="200" stroke="#4A90D9" strokeWidth="0.5" opacity="0.1"/>
        <line x1="100" y1="450" x2="200" y2="500" stroke="#4A90D9" strokeWidth="0.5" opacity="0.12"/>
        <line x1="200" y1="500" x2="160" y2="560" stroke="#4A90D9" strokeWidth="0.5" opacity="0.1"/>
        <line x1="650" y1="420" x2="750" y2="480" stroke="#4A90D9" strokeWidth="0.5" opacity="0.12"/>
        <line x1="750" y1="480" x2="700" y2="550" stroke="#4A90D9" strokeWidth="0.5" opacity="0.1"/>
        <circle cx="80" cy="120" r="3" fill="#4A90D9" opacity="0.2"/>
        <circle cx="200" cy="80" r="4" fill="#5BA3E6" opacity="0.15"/>
        <circle cx="300" cy="150" r="2.5" fill="#4A90D9" opacity="0.12"/>
        <circle cx="150" cy="250" r="2" fill="#4A90D9" opacity="0.1"/>
        <circle cx="600" cy="100" r="3.5" fill="#4A90D9" opacity="0.18"/>
        <circle cx="720" cy="160" r="3" fill="#5BA3E6" opacity="0.15"/>
        <circle cx="680" cy="280" r="2" fill="#4A90D9" opacity="0.1"/>
        <circle cx="550" cy="200" r="2.5" fill="#4A90D9" opacity="0.12"/>
        <circle cx="100" cy="450" r="3" fill="#4A90D9" opacity="0.15"/>
        <circle cx="200" cy="500" r="2.5" fill="#5BA3E6" opacity="0.12"/>
        <circle cx="160" cy="560" r="2" fill="#4A90D9" opacity="0.1"/>
        <circle cx="650" cy="420" r="3" fill="#4A90D9" opacity="0.15"/>
        <circle cx="750" cy="480" r="2.5" fill="#5BA3E6" opacity="0.12"/>
        <circle cx="700" cy="550" r="2" fill="#4A90D9" opacity="0.1"/>
        <circle cx="50" cy="300" r="2" fill="#4A90D9" opacity="0.08"/>
        <circle cx="760" cy="350" r="2.5" fill="#4A90D9" opacity="0.08"/>
      </svg>

      {/* Radial glow */}
      <div style={{
        position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
        width:500, height:500, borderRadius:"50%",
        background:"radial-gradient(circle, rgba(74,144,217,0.08) 0%, transparent 70%)"
      }}/>

      {/* Login card */}
      <div style={{
        position:"relative", zIndex:1, width:400,
        background:"rgba(255,255,255,0.97)", backdropFilter:"blur(20px)",
        borderRadius:16, padding:"44px 40px 36px",
        boxShadow:"0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)"
      }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <NeuroGenLogo size={56} variant="dark" subtitle="IOM QA PORTAL" centered />
        </div>

        <div style={{ height:1, background:"linear-gradient(90deg, transparent, #E2E8F0, transparent)", marginBottom:28 }}/>

        <div style={{ textAlign:"center", marginBottom:28 }}>
          <h1 style={{ fontFamily:"var(--font-sans)", fontSize:20, fontWeight:600, letterSpacing:"-0.02em", color:"var(--text-primary)", marginBottom:8 }}>
            Sign in to continue
          </h1>
          <p style={{ fontSize:13, color:"#64748B", lineHeight:1.6, margin:0 }}>
            Use your organization Microsoft account to access the QA review portal.
          </p>
        </div>

        <button
          className="btn btn-primary btn-lg"
          style={{
            width:"100%", justifyContent:"center", gap:10, marginBottom:20,
            background:"#2563EB", borderRadius:10, padding:"13px 24px",
            boxShadow:"0 4px 14px rgba(37,99,235,0.35)"
          }}
          onClick={() => instance.loginPopup(loginRequest).catch(console.error)}
        >
          <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
            <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
            <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          Continue with Microsoft
        </button>

        <div style={{ borderTop:"1px solid #F1F5F9", paddingTop:16, fontSize:11, color:"#94A3B8", fontFamily:"var(--font-mono)", lineHeight:1.7, textAlign:"center", letterSpacing:"0.02em" }}>
          MFA required · Internal use only · HIPAA compliant
        </div>
      </div>
    </div>
  );
}

// Where someone goes when a route is not theirs. An IP has no Dashboard, so
// sending them there would bounce them straight back out of it.
function useRoleHome() {
  const { isIP } = useRoles();
  return isIP ? "/documents" : "/dashboard";
}

function RequireRole({ allowed, children }) {
  const { roles } = useRoles();
  const home = useRoleHome();
  const ok = roles.some((r) =>
    allowed.some((a) => String(a).toLowerCase() === String(r).toLowerCase())
  );
  return ok ? children : <Navigate to={home} replace />;
}

function RoleHome() {
  return <Navigate to={useRoleHome()} replace />;
}

function AppShell() {
  const { instance, accounts } = useMsal();
  const user = accounts[0];
  const { roles: userRoles, isAdmin, isTech, isIP } = useRoles();
  const location = useLocation();
  const [failedCount, setFailedCount] = React.useState(0);
  const { batches, singles } = useUpload();

  const refreshCount = React.useCallback(() => {
    if (isTech && !isAdmin) { setFailedCount(0); return; }
    import("./lib/api").then(({ getDocuments }) => {
      getDocuments().then(docs => {
        // Both statuses are awaiting a human: PENDING_REVIEW is a case with
        // failures, PASSED_NO_REVIEW one with none. Passing every check does
        // not release a case, a reviewer does, so the badge has to count both
        // or it disagrees with the Ready for review count on All Cases — and
        // the ones it left out were exactly the auto-passed cases, the set
        // least likely to have had eyes on them.
        setFailedCount(docs.filter(d => (d.reviewerStatus === "PENDING_REVIEW"
            || d.reviewerStatus === "PASSED_NO_REVIEW")
          && !(d.sourceType && d.sourceType !== "Combined" && d.sourceType !== "PID_QA")).length);
      }).catch(() => {});
    });
  }, [isTech, isAdmin]);

  // Fetch on mount only — there is no interval here despite what this comment
  // used to claim. The badge is also refreshed when an upload completes, just
  // below. Between those it goes stale, so it will not tick down as reviewers
  // sign cases off. Adding an interval is not as cheap as it looks: this reads
  // the whole documents feed to derive one integer, so polling it would pull
  // several MB per tab per tick. A count endpoint would be the way to do it.
  React.useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  // Refresh when any upload completes
  const prevCompleted = React.useRef(0);
  React.useEffect(() => {
    const completed = batches.filter(b => b.status === "COMPLETE").length
                    + singles.filter(s => s.status === "COMPLETE").length;
    if (completed > prevCompleted.current) {
      prevCompleted.current = completed;
      setTimeout(refreshCount, 5000);
    }
  }, [batches, singles, refreshCount]);

  // Pass current doc id to floating agent when on a document detail page
  const docIdMatch = location.pathname.match(/^\/documents\/(.+)$/);
  const currentDocId = docIdMatch ? decodeURIComponent(docIdMatch[1]) : null;

  return (
    <div className="app-shell">
        <Sidebar user={user} onLogout={() => instance.logoutPopup()} failedCount={failedCount} />
        <div className="main-content">
          <Routes>
            <Route path="/"              element={<RoleHome />} />
            <Route path="/dashboard"     element={<RequireRole allowed={["Admin", "SuperAdmin", "Tech", "Member"]}><Dashboard /></RequireRole>} />
            <Route path="/documents"     element={<Documents />} />
            <Route path="/documents/:id" element={<DocumentDetail />} />
            <Route path="/upload"        element={<RequireRole allowed={["Admin"]}><Upload /></RequireRole>} />
            <Route path="/agent"         element={<RequireRole allowed={["Admin"]}><AgentChat /></RequireRole>} />
            <Route path="/guide"         element={<RequireRole allowed={["Admin"]}><Guide /></RequireRole>} />
            <Route path="/audit"         element={<RequireRole allowed={["Admin"]}><Audit /></RequireRole>} />
            <Route path="/my-report"     element={<RequireRole allowed={["Tech", "Admin"]}><MyReport /></RequireRole>} />
            <Route path="/admin/users"   element={<RequireRole allowed={["SuperAdmin"]}><AdminUsers /></RequireRole>} />
            <Route path="/settings/qa-checks" element={<RequireRole allowed={["Admin"]}><AdminQAChecks /></RequireRole>} />
            <Route path="/settings/idr-prompt" element={<RequireRole allowed={["Admin"]}><AdminIdrPrompt /></RequireRole>} />
            <Route path="/audit-files"   element={<RequireRole allowed={["Admin"]}><AuditPreview /></RequireRole>} />
            <Route path="/ip-reporting"  element={<RequireRole allowed={["Admin"]}><IPReporting isAdmin={isAdmin} /></RequireRole>} />
            {/* A physician's own report, on its own route. The admin page
                compares readers against each other and exports them; this
                is the same component with none of that reachable. */}
            <Route path="/my-ip-report" element={<RequireRole allowed={["IP"]}><IPReporting isAdmin={false} /></RequireRole>} />
            <Route path="/idr"           element={<RequireRole allowed={["Admin"]}><IDRReporting isAdmin={isAdmin} /></RequireRole>} />
            <Route path="/mso"           element={<RequireRole allowed={["Admin"]}><MSOReporting /></RequireRole>} />
            <Route path="*"              element={<RoleHome />} />
          </Routes>
        </div>
        {/* The AI assistant is an admin tool. isTech is the effective role, so an
            admin viewing as a tech sees what the tech sees. An IP never gets it:
            it answers across every case. */}
        {!(isTech && !isAdmin) && !isIP && <FloatingAgent docId={currentDocId} />}
        <UploadStatusBar />
      </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <UploadProvider>
          <AuthenticatedTemplate>
            <TechIdentityProvider><AppShell /></TechIdentityProvider>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate><LoginPage /></UnauthenticatedTemplate>
        </UploadProvider>
      </MsalProvider>
    </BrowserRouter>
  );
}

export async function recoverBatch(batchId) {
  const res = await fetch(`${API_BASE}/batch/${batchId}/recover`, {
    method: "POST",
    headers: authHeaders(),
  });
  return res.json();
}
